import { computeReorder, type MoveTarget } from "./asset-reorder";
import { fetchWithOOB, getCSRFToken } from "./htmx-helpers";
import { alpineData } from "./alpine-data";
import { studioConfirm } from "./studio-confirm";

// selection-manager.ts is the MINIMAL manage-page glue for a selection
// gallery: reorder ("sort mode") only. Unlike deliverable-manager.ts (~480
// lines: groups, rename, replace, content editing, ...), a selection gallery
// has no group indirection and none of that surrounding chrome, so this file
// ports only the sort-mode subset built around the shared, pure
// `computeReorder` helper (asset-reorder.ts) — reused as-is, never
// reimplemented.
//
// Convention this component expects from its host template (Task 14 wires
// the actual x-data + markup):
//   - a container element `id="selection-content-<galleryId>"` wrapping the
//     asset tiles, each tile a `[data-asset-wrapper="<assetId>"]` element
//     (mirrors deliverable_manage.templ's `group-content-<groupId>` +
//     `data-asset-wrapper` pairing);
//   - each tile focusable via `id="sortable-<assetId>"` (mirrors
//     deliverable-manager.ts's `sortable-<assetId>` focus target).

// selectionManagerState is exported (not just the Alpine.data factory) so a
// unit test can construct the raw state object and call its move methods
// directly, without bootstrapping Alpine. Every Alpine "magic" access
// ($nextTick) is optionally-guarded so the pure reorder logic — the only
// thing under test — never touches `this.$nextTick` or the DOM outside a
// real browser/Alpine runtime.
export function selectionManagerState(galleryId: string, deleteConfirmMsg = "") {
  return alpineData({
    galleryId,
    deleteConfirmMsg,
    sortMode: false,
    orderDirty: false,
    assetOrder: [] as { id: string }[],
    originalOrder: [] as { id: string }[],
    selectedSortIds: [] as string[],
    sortAnchorId: null as string | null,
    deleteMode: false,
    deleteSelectedIds: [] as string[],

    enterSortMode() {
      this.sortMode = true;
      this.deleteMode = false;
      this.orderDirty = false;
      this.selectedSortIds = [];
      this.sortAnchorId = null;
      const container = document.getElementById(`selection-content-${this.galleryId}`);
      if (!container) return;

      this.assetOrder = Array.from(container.querySelectorAll("[data-asset-wrapper]")).map(
        (el) => ({ id: (el as HTMLElement).dataset.assetWrapper as string }),
      );
      this.originalOrder = JSON.parse(JSON.stringify(this.assetOrder));
    },

    exitSortMode(saved = false) {
      this.sortMode = false;
      this.orderDirty = false;
      this.selectedSortIds = [];
      this.sortAnchorId = null;
      if (!saved) {
        // Restore the pre-edit order. The grid's x-effect
        // (selection_gallery.templ) reacts to this assetOrder reset — and to
        // sortMode flipping false — and physically re-lays the DOM back to the
        // original sequence; no imperative DOM call here.
        this.assetOrder = JSON.parse(JSON.stringify(this.originalOrder));
      }
    },

    selectSingle(id: string) {
      this.selectedSortIds = [id];
      this.sortAnchorId = id;
    },

    // moveSelection is the pure-logic core: it delegates entirely to
    // computeReorder for the "what should the new order be" question, and
    // only handles local state + (browser-only) DOM/focus side effects.
    moveSelection(target: MoveTarget) {
      if (this.selectedSortIds.length === 0) return;
      const ids = this.assetOrder.map((a) => a.id);
      const { order, leadingId, changed } = computeReorder(ids, this.selectedSortIds, target);
      if (!changed) return;

      this.assetOrder = order.map((id) => ({ id }));
      this.orderDirty = true;
      // The grid's x-effect (selection_gallery.templ) reads assetOrder across
      // the x-data boundary and re-lays the DOM to match within Alpine's
      // reactive flush. $nextTick below then runs AFTER that DOM update, so the
      // focus target exists in its new position.

      if (leadingId && typeof this.$nextTick === "function") {
        this.$nextTick(() => {
          const el = document.getElementById(`sortable-${leadingId}`) as HTMLElement | null;
          if (!el) return;
          el.focus({ preventScroll: true });
          // Fix carried over from deliverable-manager.ts: without an explicit
          // block:"nearest" the browser's default focus-scroll can yank the
          // whole page to center the tile, which reads as a jump on a long grid.
          el.scrollIntoView({ block: "nearest", inline: "nearest" });
        });
      }
    },

    // Per-tile buttons: act on the selection if the clicked tile is part of
    // it, otherwise make it the sole selection first.
    buttonMove(id: string, target: MoveTarget) {
      if (!this.selectedSortIds.includes(id)) this.selectSingle(id);
      this.moveSelection(target);
    },

    // NOTE (reorder DOM/tab order): the physical DOM reorder that makes
    // keyboard Tab/Shift+Tab and reading order follow the visible order is no
    // longer done imperatively from here. It is driven by an x-effect on the
    // grid element (selection_gallery.templ) which calls the selectionGallery
    // component's applyDOMOrder(). An imperative appendChild from this scope
    // did NOT reliably reorder the grid in this nested selectionGallery-x-data
    // + poll-wrapper structure (it produced no movement at all — see git
    // history f9262f59c "mirror deliverable" and its revert faf6e2e77), even
    // though the byte-identical approach works for the deliverable grid, which
    // has no intervening x-data/poll layer. Alpine's own reactive effects DO
    // run reliably here (the CSS `order` :style binding, which reads this
    // scope's assetOrder across the x-data boundary, has always worked), so we
    // reorder the DOM the same reactive way.

    async saveOrder() {
      // SelectionService.UpdateAssetOrder takes a plain ordered ID slice
      // (array index IS the new order) — unlike DeliverableService, which
      // takes []AssetOrderItem with an explicit display_order per item. See
      // the comment on SelectionHandler.UpdateAssetOrder in
      // internal/api/selection_asset.go.
      const savedIds = this.assetOrder.map((a: { id: string }) => a.id);
      await fetchWithOOB("PUT", `/api/selection-galleries/${this.galleryId}/asset-order`, {
        order: savedIds,
      });

      this.originalOrder = JSON.parse(JSON.stringify(this.assetOrder));
      // Announce the new order BEFORE leaving sort mode. selectionGallery
      // listens for this and re-stamps its client assets' display_order to the
      // saved sequence (applySavedOrder), so once sortMode flips false the
      // default-lens DOM reorder the grid's x-effect applies matches what was
      // just persisted instead of snapping back to the pre-save order. The same
      // event also drives the grid's poll refresh (hx-trigger in
      // selection_gallery.templ).
      if (typeof document !== "undefined") {
        // bubbles:true is REQUIRED: this is dispatched on document.body, and
        // selectionGallery's handler is `x-on:selection-asset-order-updated.window`
        // (Alpine listens on window). A non-bubbling event fired on body never
        // reaches window, so applySavedOrder would never run — leaving the
        // client assets' display_order stale and the grid's x-effect snapping
        // the DOM back to the pre-save order after sort mode exits. (htmx's
        // `from:body` poll trigger catches it either way.)
        document.body.dispatchEvent(
          new CustomEvent("selection-asset-order-updated", {
            detail: { order: savedIds },
            bubbles: true,
          }),
        );
      }
      this.exitSortMode(true);
    },

    enterDeleteMode() {
      this.deleteMode = true;
      this.deleteSelectedIds = [];
      this.sortMode = false;
    },

    exitDeleteMode() {
      this.deleteMode = false;
      this.deleteSelectedIds = [];
    },

    toggleDeleteSelect(id: string) {
      const idx = this.deleteSelectedIds.indexOf(id);
      if (idx === -1) this.deleteSelectedIds = [...this.deleteSelectedIds, id];
      else this.deleteSelectedIds = this.deleteSelectedIds.filter((existing) => existing !== id);
    },

    isDeleteSelected(id: string): boolean {
      return this.deleteSelectedIds.includes(id);
    },

    async confirmDelete() {
      if (this.deleteSelectedIds.length === 0) return;
      if (!(await studioConfirm(this.deleteConfirmMsg))) return;
      // Async confirm: the selection may have been cleared while it was open.
      if (this.deleteSelectedIds.length === 0) return;
      await fetchWithOOB("DELETE", `/api/selection-galleries/${this.galleryId}/assets`, {
        asset_ids: this.deleteSelectedIds,
      });
      this.exitDeleteMode();
      if (typeof document !== "undefined") {
        document.body.dispatchEvent(new CustomEvent("assets-deleted"));
      }
    },
  });
}

export const registerSelectionManager = (Alpine: any) => {
  Alpine.data("selectionManager", (galleryId: string, deleteConfirmMsg = "") =>
    selectionManagerState(galleryId, deleteConfirmMsg),
  );
};

// --- selectionGallery: the client-facing selection store -------------------

export type SortLens = "default" | "first" | "last";

export interface SelectionGalleryAsset {
  id: string;
  status: string;
  display_order?: number;
  selected?: boolean;
  [key: string]: unknown;
}

function sortLensStorageKey(galleryId: string): string {
  return `selection_gallery_sort_lens_${galleryId}`;
}

function loadSortLens(galleryId: string): SortLens {
  try {
    const stored = localStorage.getItem(sortLensStorageKey(galleryId));
    if (stored === "first" || stored === "last" || stored === "default") return stored;
  } catch {
    /* localStorage unavailable (private mode, disabled) — fall back to default */
  }
  return "default";
}

// selectionGallery is the client-side selection store for the visitor-facing
// selection gallery: which assets are picked, in what lens order, and the
// completed-only counter that must agree with the manage page's server-side
// count. Mirrors how deliverable-gallery.ts is seeded (initial data +ids
// passed in by the templ via util.ToJSON); Task 14 wires the actual x-data call.
//
// lightboxOpen/currentAsset/openLightbox(ByID)/next/prev were added
// alongside Task 14's templates (not part of Task 13's original scope) --
// every other gallery-owning Alpine component in this codebase
// (deliverable-gallery.ts, project-detail.ts) independently carries this
// same small subset because Lightbox (lightbox.templ) is shared markup that
// expects it on whatever scope embeds it; selection_gallery.templ embeds
// Lightbox the same way, so without this the grid's "click image to open
// lightbox" affordance would throw ReferenceErrors instead of working.
// next()/prev() deliberately walk sortedAssets (not assets) — the design
// spec pins lightbox next/prev to "the currently displayed (sorted) order".
export default (initialAssets: SelectionGalleryAsset[], galleryId: string) =>
  alpineData({
    galleryId,
    assets: initialAssets,
    selectedIds: new Set<string>(
      initialAssets.filter((a) => a.selected).map((a) => a.id),
    ) as Set<string>,
    sortLens: loadSortLens(galleryId) as SortLens,
    lightboxOpen: false,
    currentAsset: null as SelectionGalleryAsset | null,
    _sortedCache: null as SelectionGalleryAsset[] | null,
    _sortedCacheLens: null as SortLens | null,

    init() {
      this._resortCache();
    },

    get selectedCount(): number {
      return this.selectedIds.size;
    },

    // total MUST count only completed assets — matches the server view-model's
    // CountCompleted, which is what the manage-page readout is built from.
    // Counting pending/uploading assets here would make the two disagree.
    get total(): number {
      return this.assets.filter((a: SelectionGalleryAsset) => a.status === "completed").length;
    },

    // Re-sorts only when sortLens actually changed (not on every render/toggle)
    // — the sorted array is cached and only recomputed by setSortLens below.
    get sortedAssets(): SelectionGalleryAsset[] {
      if (!this._sortedCache || this._sortedCacheLens !== this.sortLens) {
        this._resortCache();
      }
      return this._sortedCache as SelectionGalleryAsset[];
    },

    _resortCache() {
      const list = [...this.assets];
      const byDisplayOrder = (a: SelectionGalleryAsset, b: SelectionGalleryAsset) =>
        (a.display_order ?? 0) - (b.display_order ?? 0);

      if (this.sortLens === "first" || this.sortLens === "last") {
        // Selection state (live selectedIds, not any stale per-asset
        // `.selected` field) is the PRIMARY sort key -- "Selected first"/
        // "Selected last" must group by selection, not merely reverse
        // display_order. display_order is the secondary key, within each
        // group.
        const group = (a: SelectionGalleryAsset) => (this.selectedIds.has(a.id) ? 0 : 1);
        const wantSelectedFirst = this.sortLens === "first";
        list.sort((a: SelectionGalleryAsset, b: SelectionGalleryAsset) => {
          const ga = group(a);
          const gb = group(b);
          if (ga !== gb) return wantSelectedFirst ? ga - gb : gb - ga;
          return byDisplayOrder(a, b);
        });
      } else {
        list.sort(byDisplayOrder);
      }
      this._sortedCache = list;
      this._sortedCacheLens = this.sortLens;
    },

    setSortLens(lens: SortLens) {
      if (lens === this.sortLens) return;
      this.sortLens = lens;
      try {
        localStorage.setItem(sortLensStorageKey(this.galleryId), lens);
      } catch {
        /* best-effort persistence only */
      }
      this._resortCache();
    },

    // applyDOMOrder physically reorders the grid's wrapper elements to match
    // `ids` so keyboard Tab/Shift+Tab and screen-reader reading order follow
    // the visible order. It is invoked from an x-effect on the grid element
    // (selection_gallery.templ): `applyDOMOrder(sortMode ? assetOrder ids :
    // sortedAssets ids)`. Doing the reorder inside a reactive effect (rather
    // than an imperative call from each mutation) is what makes it work in this
    // nested structure — the effect re-runs within Alpine's reactive flush
    // whenever the order changes, including assetOrder mutations that occur on
    // the parent selectionManager scope (Alpine tracks reactive reads across
    // the x-data boundary; the CSS `order` binding relies on the same). The
    // former imperative updateDOM() did not reliably move the DOM here (see git
    // f9262f59c/faf6e2e77). CSS `order` still drives the instant visual; this
    // aligns DOM source order to it.
    applyDOMOrder(ids: string[]) {
      if (typeof document === "undefined" || !Array.isArray(ids)) return;
      const container = document.getElementById(`selection-content-${this.galleryId}`);
      if (!container) return;
      ids.forEach((id) => {
        const el = container.querySelector(`[data-asset-wrapper="${id}"]`);
        if (el) container.appendChild(el);
      });
    },

    // After a manager saves a new order, the server persists it and the poll
    // re-renders the grid in that order — but this client component's `assets`
    // still carry their old display_order. Left unfixed, the default lens (and
    // therefore the DOM reorder the grid's x-effect applies once sort mode
    // exits) would snap back to the pre-save order until a full reload. Re-stamp
    // display_order from the saved id sequence and re-sort so the default lens
    // agrees with what was just persisted. Dispatched from
    // selectionManagerState.saveOrder before it leaves sort mode.
    applySavedOrder(order: string[]) {
      if (!Array.isArray(order) || order.length === 0) return;
      const rank = new Map(order.map((id, i) => [id, i] as const));
      this.assets = this.assets.map((a: SelectionGalleryAsset) =>
        rank.has(a.id) ? { ...a, display_order: rank.get(a.id) as number } : a,
      );
      this._resortCache();
    },

    // Selection-dependent lenses ('first'/'last') group by LIVE selectedIds,
    // but the sortedAssets cache only invalidates on a lens change (see its
    // getter) -- a plain selection toggle wouldn't otherwise recompute the
    // order. Re-sorting reassigns _sortedCache, which the grid's x-effect reads
    // reactively and re-lays the DOM from. Only needed when the active lens
    // actually depends on selection state.
    _reorderIfLensActive() {
      if (this.sortLens === "default") return;
      this._resortCache();
    },

    isSelected(assetId: string): boolean {
      return this.selectedIds.has(assetId);
    },

    // Optimistic toggle: flip the local set immediately so the UI responds
    // instantly, then persist server-side; roll back on failure so the client
    // never drifts from the server's authoritative selection state.
    async toggleSelect(assetId: string) {
      const wasSelected = this.selectedIds.has(assetId);
      const next = new Set(this.selectedIds);
      if (wasSelected) next.delete(assetId);
      else next.add(assetId);
      this.selectedIds = next;
      this._reorderIfLensActive();

      try {
        const resp = await fetch(
          `/api/selection-galleries/${this.galleryId}/assets/${assetId}/selection`,
          {
            method: "PUT",
            credentials: "same-origin",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": getCSRFToken(),
            },
            body: JSON.stringify({ selected: !wasSelected }),
          },
        );
        if (!resp.ok) throw new Error(`selection update failed: ${resp.status}`);
      } catch (err) {
        console.error("[selectionGallery] toggleSelect failed, rolling back", err);
        const rollback = new Set(this.selectedIds);
        if (wasSelected) rollback.add(assetId);
        else rollback.delete(assetId);
        this.selectedIds = rollback;
        this._reorderIfLensActive();
      }
    },

    openLightbox(asset: SelectionGalleryAsset) {
      this.currentAsset = asset;
      this.lightboxOpen = true;
    },

    openLightboxByID(id: string) {
      const asset = this.assets.find((a: SelectionGalleryAsset) => a.id === id);
      if (asset) this.openLightbox(asset);
    },

    // next()/prev() follow the currently displayed (sorted) order and only
    // ever land on a completed asset — matching the grid tile's own
    // clickability rule (processing tiles never call openLightboxByID).
    next() {
      const media = this.sortedAssets.filter(
        (a: SelectionGalleryAsset) => a.status === "completed",
      );
      if (media.length === 0) return;
      const idx = media.findIndex(
        (a: SelectionGalleryAsset) => a.id === this.currentAsset?.id,
      );
      this.currentAsset = idx === -1 ? media[0] : media[(idx + 1) % media.length];
    },

    prev() {
      const media = this.sortedAssets.filter(
        (a: SelectionGalleryAsset) => a.status === "completed",
      );
      if (media.length === 0) return;
      const idx = media.findIndex(
        (a: SelectionGalleryAsset) => a.id === this.currentAsset?.id,
      );
      this.currentAsset =
        idx === -1 ? media[media.length - 1] : media[(idx - 1 + media.length) % media.length];
    },
  });
